// src/systems/chess.ts
//
// Phase 14 (CHESS-01): pure dimension-only chess movement geometry.
// No board state, no blockers, no chess rules — only "from this origin,
// with this piece, which squares lie on a rows×cols grid".
//
// Consumed by src/systems/placements.ts (Plan 14-02) to translate a
// chess fiche placement into the Bet[] it covers.

export type ChessPiece = 'pawn' | 'knight' | 'bishop' | 'rook' | 'queen' | 'king';

export interface Square {
  row: number;
  col: number;
}

export interface GridDims {
  rows: number;
  cols: number;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function inBounds(s: Square, dims: GridDims): boolean {
  return s.row >= 0 && s.row < dims.rows && s.col >= 0 && s.col < dims.cols;
}

/** Walk a single ray from origin+step, stopping before any blocker square. */
function walkRay(
  origin: Square,
  dr: number,
  dc: number,
  dims: GridDims,
  blockers: ReadonlySet<string> = new Set(),
): Square[] {
  const squares: Square[] = [];
  let r = origin.row + dr;
  let c = origin.col + dc;
  while (r >= 0 && r < dims.rows && c >= 0 && c < dims.cols) {
    if (blockers.has(`${r},${c}`)) break; // stop before the blocking fiche (not included)
    squares.push({ row: r, col: c });
    r += dr;
    c += dc;
  }
  return squares;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Return every square reachable by `piece` from `origin` on a `dims.rows × dims.cols` grid.
 *
 * `blockers` is an optional set of "row,col" keys for squares occupied by other fiches.
 * - Sliding pieces (bishop/rook/queen): rays stop BEFORE a blocker (blocker not included).
 * - Jump/step pieces (knight/pawn/king): blocker squares are excluded from the result.
 * - The origin square is NEVER included in the result.
 */
export function reachable(
  piece: ChessPiece,
  origin: Square,
  dims: GridDims,
  blockers: ReadonlySet<string> = new Set(),
): Square[] {
  switch (piece) {
    case 'pawn': {
      const candidates: Square[] = [
        { row: origin.row - 1, col: origin.col },
        { row: origin.row - 1, col: origin.col - 1 },
        { row: origin.row - 1, col: origin.col + 1 },
      ];
      return candidates.filter((s) => inBounds(s, dims) && !blockers.has(`${s.row},${s.col}`));
    }

    case 'knight': {
      const KNIGHT_DELTAS: [number, number][] = [
        [-2, -1],
        [-2, 1],
        [-1, -2],
        [-1, 2],
        [1, -2],
        [1, 2],
        [2, -1],
        [2, 1],
      ];
      return KNIGHT_DELTAS.map(([dr, dc]) => ({
        row: origin.row + dr,
        col: origin.col + dc,
      })).filter((s) => inBounds(s, dims) && !blockers.has(`${s.row},${s.col}`));
    }

    case 'bishop': {
      const DIAG_RAYS: [number, number][] = [
        [-1, -1],
        [-1, 1],
        [1, -1],
        [1, 1],
      ];
      return DIAG_RAYS.flatMap(([dr, dc]) => walkRay(origin, dr, dc, dims, blockers));
    }

    case 'rook': {
      const ORTH_RAYS: [number, number][] = [
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
      ];
      return ORTH_RAYS.flatMap(([dr, dc]) => walkRay(origin, dr, dc, dims, blockers));
    }

    case 'queen': {
      const ALL_RAYS: [number, number][] = [
        [-1, -1],
        [-1, 1],
        [1, -1],
        [1, 1],
        [-1, 0],
        [1, 0],
        [0, -1],
        [0, 1],
      ];
      return ALL_RAYS.flatMap(([dr, dc]) => walkRay(origin, dr, dc, dims, blockers));
    }

    case 'king': {
      const KING_DELTAS: [number, number][] = [
        [-1, -1],
        [-1, 0],
        [-1, 1],
        [0, -1],
        [0, 1],
        [1, -1],
        [1, 0],
        [1, 1],
      ];
      return KING_DELTAS.map(([dr, dc]) => ({
        row: origin.row + dr,
        col: origin.col + dc,
      })).filter((s) => inBounds(s, dims) && !blockers.has(`${s.row},${s.col}`));
    }
  }
}
